import { getTelehealthConfig } from '../../../config';
import { logger } from '../../../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../../../events';
import { UserType } from '../../../types';
import { normalizeError, withRetry } from '../../../utils';
import { DebugMutex } from '../../core/mutex';
import { parseIdentity } from '../../core/room/participant/utils';
import type { VideoClientEvent, VideoClientEventHandler } from '../VideoClientEvents';
import { ZoomEventBus } from '../ZoomEventBus';
import { VideoClient } from '../types';

const LIVE_TRANSCRIPTION_CAPTION_MESSAGE_EVENT: VideoClientEvent = 'caption-message';

type LiveTranscriptionMessage = Parameters<VideoClientEventHandler<typeof LIVE_TRANSCRIPTION_CAPTION_MESSAGE_EVENT>>[0];

export type TranscriptSentenceMessage = {
  appointmentShortId: string;
  messageId: string;
  participantUserId: string;
  participantIdentity: string;
  timestampMs: number;
  userType: UserType;
  content: string;
};

export type SendLiveTranscriptionBatch = (batch: TranscriptSentenceMessage[]) => Promise<void>;

export class LiveTranscriptionController {
  readonly #appointmentShortId: string;
  readonly #videoClient: VideoClient;
  readonly #eventBus: ZoomEventBus;
  readonly #sendBatch: SendLiveTranscriptionBatch;
  readonly #flushIntervalMs: number;

  #isStarted: boolean = false;
  #buffer: TranscriptSentenceMessage[] = [];
  #flushMutex = new DebugMutex('LiveTranscriptionController.flushMutex');
  #flushTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    appointmentShortId: string,
    videoClient: VideoClient,
    eventBus: ZoomEventBus,
    sendBatch: SendLiveTranscriptionBatch,
    flushIntervalMs: number = getTelehealthConfig().zoom_live_transcription_config.flushIntervalMs,
  ) {
    this.#appointmentShortId = appointmentShortId;
    this.#videoClient = videoClient;
    this.#eventBus = eventBus;
    this.#sendBatch = sendBatch;
    this.#flushIntervalMs = flushIntervalMs;
  }

  async start() {
    if (this.#isStarted) return;

    this.#flushTimer = setInterval(() => {
      this.#scheduleFlush();
    }, this.#flushIntervalMs);

    try {
      await this.#videoClient.getLiveTranscriptionClient().startLiveTranscription();
      this.#eventBus.on(LIVE_TRANSCRIPTION_CAPTION_MESSAGE_EVENT, this.#handleCaptionMessage);
      this.#isStarted = true;
    } catch (error) {
      logger.error(
        'Failed to start Zoom live transcription',
        { appointmentShortId: this.#appointmentShortId },
        normalizeError(error),
      );
      this.#isStarted = false;
      if (this.#flushTimer) {
        clearInterval(this.#flushTimer);
        this.#flushTimer = null;
      }
      throw error;
    }

    sendLoggingEvents(
      TrackingEvents.ZOOM_LIVE_TRANSCRIPTION_STARTED,
      { appointmentShortId: this.#appointmentShortId },
      {
        logLevel: 'info',
        message: 'Zoom live transcription started for appointment',
      },
    );
  }

  async stop() {
    if (!this.#isStarted) return;

    // Clear the flush timer to prevent any further flushes
    if (this.#flushTimer) {
      clearInterval(this.#flushTimer);
      this.#flushTimer = null;
    }

    // Flush the buffer one last time
    try {
      await withRetry(() => this.#scheduleFlush({ throwOnFailure: true }), {
        retries: 2,
      })();
    } catch (error) {
      logger.error(
        'Live transcription final flush failed',
        { appointmentShortId: this.#appointmentShortId },
        normalizeError(error),
      );
    }

    // Disable the live transcription and unregister the caption message handler
    try {
      this.#eventBus.off(LIVE_TRANSCRIPTION_CAPTION_MESSAGE_EVENT, this.#handleCaptionMessage);
      logger.info('Zoom live transcription stopped for appointment', { appointmentShortId: this.#appointmentShortId });
    } catch (error) {
      logger.warn(
        'Failed to disable Zoom captions on stop',
        { appointmentShortId: this.#appointmentShortId },
        normalizeError(error),
      );
      throw error;
    } finally {
      this.#isStarted = false;
    }
  }

  #handleCaptionMessage: VideoClientEventHandler<typeof LIVE_TRANSCRIPTION_CAPTION_MESSAGE_EVENT> = (
    message: LiveTranscriptionMessage,
  ) => {
    if (message.done !== true) return;

    const userTypeResult = this.#resolveUserType(message.userId);
    if (!userTypeResult) {
      logger.error('Dropping caption for unknown participant', {
        appointmentShortId: this.#appointmentShortId,
        userId: message.userId,
      });
      return;
    }

    this.#buffer.push({
      appointmentShortId: this.#appointmentShortId,
      messageId: message.msgId,
      participantUserId: userTypeResult.participantUserId, // short id
      participantIdentity: userTypeResult.participantIdentity, // CLIENT|short_id or PROVIDER|short_id
      timestampMs: message.timestamp,
      userType: userTypeResult.userType,
      content: message.text,
    });
  };

  #scheduleFlush({ throwOnFailure = false }: { throwOnFailure?: boolean } = {}): Promise<void> {
    return this.#flushMutex.runExclusive(
      () => this.#flushBuffer({ throwOnFailure }),
      'LiveTranscriptionController.flushBuffer',
    );
  }

  async #flushBuffer({ throwOnFailure = false }: { throwOnFailure?: boolean } = {}) {
    if (this.#buffer.length === 0) return;

    const batch = this.#buffer;
    this.#buffer = [];

    try {
      await this.#sendBatch(batch);
    } catch (error) {
      logger.warn('[live-transctiption] failed to send batch to server, restoring buffer', {
        appointmentShortId: this.#appointmentShortId,
      });
      this.#buffer = [...batch, ...this.#buffer];
      if (throwOnFailure) {
        throw error;
      }
    }
  }

  #resolveUserType(
    userId: number,
  ): { userType: UserType; participantUserId: string; participantIdentity: string } | null {
    const participant = this.#videoClient.getUser(userId);
    if (!participant?.userIdentity) return null;

    const { userType, id } = parseIdentity(participant.userIdentity);
    return { userType, participantUserId: id, participantIdentity: participant.userIdentity };
  }
}
